import { type KeyboardEventHandler, useRef } from 'react';
import { SearchIcon } from '../icons/react/search.tsx';
import { XmarkCircleIcon } from '../icons/react/xmark-circle.tsx';
import { Spinner } from '../spinner/spinner.tsx';
import { useComposedRefs } from '../utils/use-composed-refs.ts';
import { useControllableState } from '../utils/use-controllable-state.tsx';
import {
  inputV2Clear,
  searchInputV2Lead,
  searchInputV2LeadHidden,
} from './input-v2.css.ts';
import { InputV2, type InputV2Props } from './input-v2.tsx';

export type SearchInputV2Props = Omit<
  InputV2Props,
  'before' | 'after' | 'value'
> & {
  onQueryChange: (value: string) => void;
  value?: string;
  defaultValue?: string;
  loading?: boolean;
};

export const SearchInputV2 = ({
  value,
  onChange,
  onQueryChange: onQueryChangeProp,
  defaultValue = '',
  ref,
  loading,
  onKeyDown,
  ...props
}: SearchInputV2Props) => {
  const [query, onQueryChange] = useControllableState({
    value,
    onChange: onQueryChangeProp,
    defaultValue,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const setRef = useComposedRefs(ref, inputRef);

  const onInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Escape' && query) {
      onQueryChange('');
    }

    onKeyDown?.(e);
  };

  return (
    <InputV2
      ref={setRef}
      role="searchbox"
      value={query}
      onChange={(e) => {
        onQueryChange(e.target.value);
        onChange?.(e);
      }}
      onKeyDown={onInputKeyDown}
      before={
        <span className={searchInputV2Lead}>
          <SearchIcon
            width={20}
            height={20}
            className={loading ? searchInputV2LeadHidden : undefined}
          />
          {loading ? <Spinner size={20} /> : null}
        </span>
      }
      after={
        query.length > 0 ? (
          <button
            type="button"
            className={inputV2Clear}
            aria-label="Clear search"
            onClick={() => {
              onQueryChange('');
              inputRef.current?.focus();
            }}
          >
            <XmarkCircleIcon />
          </button>
        ) : null
      }
      autoComplete="off"
      {...props}
    />
  );
};
